/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

// Contiguity guard shared by the time-stamped sample ring buffers
// (DipoleCurrentBuffer → IR, VelocityBuffer → VDOS, PositionBuffer → MSD).
//
// Each buffer reports its sample stride as (t_last − t_first)/(N − 1), and
// the spectra / MSD lag axis are scaled by that number. The formula only
// holds for one CONTIGUOUS, uniformly sampled run. The record toggles in the
// panels and the agent tools can turn recording off and back on with the
// clock still advancing in between, the engine can be rebuilt or reset so
// the clock restarts at 0, and the time step can change mid-record. Any of
// those appends samples that break the uniform spacing: a 10 ps pause
// between two 2 ps recordings reports a 3.5× stride and every IR band lands
// at 1/3.5 of its wavenumber, while the ACF correlates samples 10 ps apart
// as neighbors.
//
// The buffers call this before every push. When the incoming timestamp does
// not continue the established spacing they clear and start a fresh run with
// that sample, so whatever is in a buffer is always one uniform series.
//
// Two tests, because the stamps alone cannot catch everything:
//  • continuesSampleRun — the INFERRED test: the gap to the previous sample
//    must be within SAMPLE_STRIDE_TOLERANCE of the previous gap. Catches
//    recording gaps and clock resets. The tolerance is loose so a sampler that
//    only ships Float32 stamps (the crystal worker) does not false-trigger on
//    rounding at long times.
//  • sampleSpacingChanged — the STATED test: a sampler that knows its spacing
//    (stride × timestep; the molecular worker ships it with every packet)
//    states it, and any change starts a new run. A timestep edit of 1 → 1.5 fs
//    moves the spacing 2 → 1.5 fs, inside the inferred tolerance, and a tape
//    holding both spacings split one 3 000 cm⁻¹ line into 3 430 + 2 573.

// A new sample continues the run if its spacing from the previous sample is
// within this factor (either way) of the stride between the previous two.
export const SAMPLE_STRIDE_TOLERANCE = 1.5;

// `t` is the ring's timestamp storage, `head` the next write slot, `filled`
// whether the ring has wrapped, `capacity` its length.
export function continuesSampleRun(
  t: Float32Array | Float64Array,
  head: number,
  filled: boolean,
  capacity: number,
  timeFs: number,
): boolean {
  const n = filled ? capacity : head;
  if (n === 0) return true;
  const last = t[(head - 1 + capacity) % capacity];
  const gap = timeFs - last;
  // Clock went backwards (reset / engine rebuild) or stood still: new run.
  if (!(gap > 0)) return false;
  if (n === 1) return true; // no stride established yet
  const stride = last - t[(head - 2 + capacity) % capacity];
  if (!(stride > 0)) return true;
  return gap <= stride * SAMPLE_STRIDE_TOLERANCE && gap >= stride / SAMPLE_STRIDE_TOLERANCE;
}

// True when a sampler states a spacing for the incoming sample that differs
// from the one the current run was recorded at. Unstated (undefined) spacing
// on either side never triggers, so samplers that do not know their spacing
// keep the inferred behavior.
export function sampleSpacingChanged(runSpacingFs: number | null, spacingFs: number | undefined): boolean {
  if (spacingFs === undefined || runSpacingFs === null) return false;
  return Math.abs(spacingFs - runSpacingFs) > 1e-9 * Math.max(spacingFs, runSpacingFs);
}
